import { useState } from "react"
import { router } from "@inertiajs/react"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { MultiSelect } from "@/components/ui/select"
import { cn } from "@/lib/cn"
import type { RoleKey } from "@/types/inertia"
import type { AgentPoolOption } from "@/types/team"

// Invite modal — comma/newline-separated email list, role radio,
// optional note. Posts to POST /settings/team. Server gates with
// authorize!(:team_invite) and the role list is pre-filtered to roles
// the inviter is allowed to assign (so the radio set narrows for
// admins vs super_admins automatically).

type Props = {
  open: boolean
  onClose: () => void
  invitableRoles: { key: RoleKey; label: string }[]
  agentPools: AgentPoolOption[]
}

export function InviteTeammateModal({ open, onClose, invitableRoles, agentPools }: Props) {
  const [emails, setEmails] = useState("")
  const [role, setRole] = useState<RoleKey>(invitableRoles[0]?.key ?? "agent")
  const [note, setNote] = useState("")
  const [poolIds, setPoolIds] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setEmails("")
    setRole(invitableRoles[0]?.key ?? "agent")
    setNote("")
    setPoolIds([])
    setError(null)
  }

  function close() {
    if (submitting) return
    reset()
    onClose()
  }

  function submit() {
    const parsed = parseEmails(emails)
    if (parsed.length === 0) {
      setError("Add at least one email address.")
      return
    }
    setSubmitting(true)
    setError(null)
    router.post("/settings/team", {
      emails: parsed,
      role,
      note: note.trim() || null,
      agent_pool_ids: poolIds,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onClose()
      },
      onError: (errs) => {
        const first = Object.values(errs ?? {})[0]
        setError(typeof first === "string" ? first : "Couldn't send invites.")
      },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Dialog open={open} onClose={close} size="md">
      <DialogHeader title="Invite teammate" subtitle="They'll get an email with a sign-up link." />
      <DialogBody>
        <Field label="Email" hint="Multiple emails: separate with commas or new lines.">
          <Textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="alice@acme.io, bob@acme.io"
            rows={3}
          />
        </Field>

        <Field label="Role" hint="Roles you can assign are limited by your own permissions.">
          <div className="space-y-2">
            {invitableRoles.length === 0 ? (
              <div className="text-sm text-app-muted">You don't have permission to invite anyone right now.</div>
            ) : invitableRoles.map((r) => (
              <label
                key={r.key}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-control border px-3 py-2 text-sm",
                  role === r.key
                    ? "border-brand-red/40 bg-brand-redSoft text-app-text"
                    : "border-border bg-white/5 text-app-muted hover:bg-white/10",
                )}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={r.key}
                  checked={role === r.key}
                  onChange={() => {
                    setRole(r.key)
                    if (r.key !== "agent" && r.key !== "agent_manager") setPoolIds([])
                  }}
                  className="accent-brand-red"
                />
                {r.label}
              </label>
            ))}
          </div>
        </Field>

        {agentPools.length > 0 && (role === "agent" || role === "agent_manager") && (
          <Field label="Agent pools (optional)" hint="Add the new member to one or more named groups used by triage policies.">
            <MultiSelect<number>
              value={agentPools.filter((p) => poolIds.includes(p.id)).map((p) => ({ value: p.id, label: p.name }))}
              onChange={(opts) => setPoolIds((opts ?? []).map((o) => o.value))}
              options={agentPools.map((p) => ({ value: p.id, label: p.name }))}
              placeholder="Select pools…"
              isClearable
            />
          </Field>
        )}

        <Field label="Note (optional)" hint="Shown in the invitation email.">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Welcome to the team — ping me on Slack with any questions."
            rows={3}
          />
        </Field>

        {error && (
          <div className="rounded-control border border-status-error/30 bg-status-error/10 px-3 py-2 text-sm text-status-error">
            {error}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={close} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} disabled={submitting || invitableRoles.length === 0}>
          {submitting ? "Sending…" : "Send invites"}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

function parseEmails(input: string): string[] {
  return input
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-app-text">{label}</div>
      {hint && <div className="mb-2 text-xs text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}
